"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatDateInterval = exports.dateIntervalFromArray = exports.dateDiff = void 0;
const date_fns_1 = require("date-fns");
const constants_1 = require("../constants");
/**
 * We have a lot of place where we interchange if we are using date strings or date objects, so using this
 * will ensure that we always are passing a Date object into date-fns methods that expect Date objects
 * This should only be used in other helpers and not directly for returning a value to Handlebars.
 * This is not an exported helper.
 * @ignore
 * @param { string | Date } dateValue String or date object
 * @returns { Date } string value converted to Date or original date object
 * @example getAsDate("2022-10-15") => Date(2022, 10, 15)
 */
const getAsDate = (dateValue) => {
    if (typeof dateValue === "string") {
        dateValue = (0, date_fns_1.parseISO)(dateValue);
    }
    return dateValue;
};
/**
 * Helper function for converting a time span into a duration of hours and minutes. Not an exported helper. Use dateDiff with "hours"
 * @ignore
 * @param { Date } endDate Later of the two dates
 * @param { Date } startDate Earlier date to be compared to
 * @returns { string } Difference in time in an HH:mm format
 * @example formatDurationAsHoursAndMinutes(new Date(2022, 10, 15, 10, 0, 0), new Date(2022, 10, 15, 12, 5, 0)) => "2:05"
 */
const formatDurationAsHoursAndMinutes = (endDate, startDate) => {
    const hours = Math.abs((0, date_fns_1.differenceInHours)(endDate, startDate));
    const minutes = Math.abs((0, date_fns_1.differenceInMinutes)(endDate, startDate)) % 60;
    return `${hours}:${String(minutes).padStart(2, "0")}`;
};
/**
 * Given two datetimes and what type of diff you want - get the difference between the provided dates
 * @param { string } diffType Options are "hours" or "days"
 * @param { string } startDate starting datetime string
 * @param { string } endDate ending datetime
 * @returns { string | number } difference between provided dates
 * @example formatDurationAsHoursAndMinutes("hours", new Date(2022, 10, 15, 10, 0, 0), new Date(2022, 10, 15, 12, 5, 0)) => "2:05"
 * @example formatDurationAsHoursAndMinutes("days", new Date(2022, 10, 15), new Date(2022, 10, 20)) => 5
 */
const dateDiff = (diffType, startDate, endDate) => {
    endDate = getAsDate(endDate);
    startDate = getAsDate(startDate);
    switch (diffType) {
        case "hours": {
            return formatDurationAsHoursAndMinutes(endDate, startDate);
        }
        case "days": {
            return (0, date_fns_1.differenceInDays)(endDate, startDate);
        }
    }
};
exports.dateDiff = dateDiff;
/**
 * Gets the min and max date (as an interval) from an array, or an array of objects
 * @param { date[] | object[] } array - an array of dates, date strings (in ISO format),
 *                                  or objects containing dates/date strings (in ISO format)
 * @param { string | undefined } key - required key if the array contains objects
 * @returns { Interval | null } - returns the result of the comparison (a date-fns interval or null)
 * @example const dateArray = [
 *      new Date(2020, 1, 10),
 *      new Date(2020, 0, 20),
 *      new Date(2020, 5, 17),
 *      new Date(2020, 10, 21)
 *    ]
 *
 *    const interval = dateIntervalFromArray(dateArray)
 *    interval.start => Date(2020, 0, 20)
 *    interval.end => Date(2020, 10, 21)
 */
const dateIntervalFromArray = (array, key) => {
    const dates = array.reduce((accumulator, value) => {
        // if the current item is an object, find its date prop
        if (typeof value === "object" && !(0, date_fns_1.isDate)(value)) {
            if (!key) {
                // without a date property we don't know what to compare
                return accumulator;
            }
            value = value[key];
        }
        // if needed, convert it to a date
        if (typeof value === "string") {
            value = getAsDate(value);
        }
        // and check that the date is valid
        if (!(0, date_fns_1.isValid)(value)) {
            return accumulator;
        }
        accumulator.push(value);
        return accumulator;
    }, []);
    // return an Interval, even if there is only one date
    return (dates === null || dates === void 0 ? void 0 : dates.length) > 0 ? { start: (0, date_fns_1.min)(dates), end: (0, date_fns_1.max)(dates) } : null;
};
exports.dateIntervalFromArray = dateIntervalFromArray;
/**
 * Formats a date-fns interval into a string. If the year is the same for start and end
 * then startDateFormat will be used. If they are not then endDateFormat will be used for both.
 * @param {Interval} interval date-fns interval
 * @param {string|undefined} separator default " - ". The separator between the start and end dates
 * @param {string|undefined} startDateFormat default "MM/dd/yyyy". The format of the start date
 *                                             if the same year as end date
 * @param {string|undefined} endDateFormat default "MM/dd/yyyy". The format of the end date
 *                                          (and of start date if its year is different)
 * @example formatDateInterval({ start: new Date(2020, 1, 10), end: new Date(2020, 2, 10) }) => "02/10/2020 - 03/10/2020"
 */
const formatDateInterval = (interval = null, separator = " - ", startDateFormat = constants_1.DATE_MM_DD_YYYY, endDateFormat = constants_1.DATE_MM_DD_YYYY) => {
    if (!interval || (!interval.start && !interval.end)) {
        return "";
    }
    let formatted = "";
    if (interval.start && (0, date_fns_1.isValid)(interval.start)) {
        if (!interval.end || (interval.end && !(0, date_fns_1.isSameYear)(interval.start, interval.end))) {
            startDateFormat = endDateFormat;
        }
        formatted = (0, date_fns_1.format)(interval.start, startDateFormat);
    }
    if (interval.end && (0, date_fns_1.isValid)(interval.end)) {
        if (formatted.length) {
            formatted = `${formatted}${separator}`;
        }
        formatted = `${formatted}${(0, date_fns_1.format)(interval.end, endDateFormat)}`;
    }
    return formatted;
};
exports.formatDateInterval = formatDateInterval;
